import { ChangeDetectionStrategy, Component, HostListener, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import {
  CalendarioAmadeus,
  CalendariosCampanha,
  DeParaIntegracao,
  PerfilAmadeus,
  PerfilInvestimentoCampanha,
  PerfilPlanoParticipante,
  PlanoCalendario,
  PlanoElegivelCampanha,
} from '../../core/models/de-para-integracao.model';
import { DeParaIntegracaoService } from '../../core/services/de-para-integracao.service';
import { ToastService } from '../../core/services/toast.service';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';

@Component({
  selector: 'app-de-para-config-modal',
  imports: [AlertDialogComponent, EmptyStateComponent],
  templateUrl: './de-para-config-modal.component.html',
  styleUrl: './de-para-config-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeParaConfigModalComponent implements OnInit {
  private readonly deParaService = inject(DeParaIntegracaoService);
  private readonly toastService = inject(ToastService);

  readonly idCampanha = input.required<number>();
  readonly close = output<void>();

  private readonly configsSignal = signal<DeParaIntegracao[]>([]);
  readonly configs = this.configsSignal.asReadonly();

  readonly aba = signal<'de-para' | 'calendario'>('de-para');

  private readonly planosCalendarioSignal = signal<PlanoCalendario[]>([]);
  readonly planosCalendario = this.planosCalendarioSignal.asReadonly();
  private readonly calendariosSignal = signal<CalendarioAmadeus[]>([]);

  /** Escolha corrente de cada plano, por NUM_PLBNF — o que o select exibe antes de salvar. */
  private readonly escolhaPorPlanoSignal = signal<Record<number, number | null>>({});
  readonly escolhaPorPlano = this.escolhaPorPlanoSignal.asReadonly();

  /** NUM_PLBNF em gravação, ou null. Um plano por vez, como o resto da modal já faz. */
  readonly salvandoCalendario = signal<number | null>(null);

  /** Plano aguardando confirmação de troca de calendário, ou null. */
  readonly trocarAlvo = signal<PlanoCalendario | null>(null);

  private readonly catalogParesSignal = signal<PerfilPlanoParticipante[]>([]);
  readonly catalogPares = this.catalogParesSignal.asReadonly();
  private readonly catalogCodigosSignal = signal<PerfilAmadeus[]>([]);

  readonly carregando = signal(false);
  readonly salvando = signal(false);
  readonly carregandoCodigos = signal(false);

  readonly formAberto = signal(false);
  readonly modoEdicao = signal(false);

  readonly perfilSelecionado = signal<PerfilInvestimentoCampanha | null>(null);
  readonly planoSelecionado = signal<PlanoElegivelCampanha | null>(null);
  readonly codigoSelecionado = signal<PerfilAmadeus | null>(null);

  readonly excluirAlvo = signal<DeParaIntegracao | null>(null);

  readonly perfisDisponiveis = computed(() => {
    const configurados = new Set(this.configs().map((c) => `${c.idPerfilInvest}-${c.numPlbnf}`));
    const vistos = new Set<number>();
    const resultado: PerfilInvestimentoCampanha[] = [];
    for (const par of this.catalogParesSignal()) {
      if (configurados.has(`${par.idPerfilInvest}-${par.numPlbnf}`) || vistos.has(par.idPerfilInvest)) {
        continue;
      }
      vistos.add(par.idPerfilInvest);
      resultado.push({ idPerfilInvest: par.idPerfilInvest, nome: par.nomePerfil });
    }
    return resultado;
  });

  readonly planosDisponiveis = computed(() => {
    const perfil = this.perfilSelecionado();
    if (!perfil) {
      return [];
    }
    const configurados = new Set(this.configs().map((c) => `${c.idPerfilInvest}-${c.numPlbnf}`));
    return this.catalogParesSignal()
      .filter((par) => par.idPerfilInvest === perfil.idPerfilInvest)
      .filter((par) => !configurados.has(`${par.idPerfilInvest}-${par.numPlbnf}`))
      .map((par) => ({ numPlbnf: par.numPlbnf, nome: par.nomePlano }));
  });

  readonly codigosDisponiveis = computed(() => {
    const plano = this.planoSelecionado();
    if (!plano) {
      return [];
    }
    const perfilAtual = this.perfilSelecionado();
    const jaConfigurados = new Set(
      this.configs()
        .filter((c) => c.numPlbnf === plano.numPlbnf && c.idPerfilInvest !== perfilAtual?.idPerfilInvest)
        .map((c) => c.codPrfinv),
    );
    return this.catalogCodigosSignal().filter((c) => !jaConfigurados.has(c.codPrfinv));
  });

  readonly podeSalvar = computed(
    () => !!this.perfilSelecionado() && !!this.planoSelecionado() && !!this.codigoSelecionado() && !this.salvando(),
  );

  ngOnInit(): void {
    this.carregarTudo();
  }

  onSelecionarAba(aba: 'de-para' | 'calendario'): void {
    this.aba.set(aba);
  }

  /** O calendário é do plano: só as opções daquele NUM_PLBNF entram no select da linha. */
  calendariosDoPlano(numPlbnf: number): CalendarioAmadeus[] {
    return this.calendariosSignal().filter((c) => c.numPlbnf === numPlbnf);
  }

  /** Rótulo curto do calendário para a confirmação de troca. */
  nomeCalendario(codCltrprf: number | null): string {
    if (codCltrprf == null) {
      return 'Sem calendário';
    }

    const calendario = this.calendariosSignal().find((c) => c.codCltrprf === codCltrprf);
    if (!calendario) {
      return `Calendário ${codCltrprf}`;
    }

    const vigencia =
      calendario.dataInicioVig || calendario.dataFimVig
        ? ` (${calendario.dataInicioVig || '—'} a ${calendario.dataFimVig || '—'})`
        : '';

    return `Calendário ${codCltrprf}${vigencia}`;
  }

  /** Nenhuma opção selecionável: a tabela só tem calendários com a vigência encerrada. */
  semCalendarioVigente(numPlbnf: number): boolean {
    const doPlano = this.calendariosDoPlano(numPlbnf);
    return doPlano.length > 0 && doPlano.every((c) => c.vencido);
  }

  onCalendarioRadioChange(numPlbnf: number, codCltrprf: number | null): void {
    this.escolhaPorPlanoSignal.update((atual) => ({ ...atual, [numPlbnf]: codCltrprf }));
  }

  /** Trocar um calendário já gravado repercute na próxima integração, então passa por confirmação. */
  onSalvarCalendarioClick(plano: PlanoCalendario): void {
    if (this.salvandoCalendario() !== null) {
      return;
    }

    if (plano.codCltrprf != null) {
      this.trocarAlvo.set(plano);
      return;
    }

    this.salvarCalendario(plano.numPlbnf);
  }

  onCancelarTroca(): void {
    this.trocarAlvo.set(null);
  }

  onConfirmarTroca(): void {
    const alvo = this.trocarAlvo();
    if (!alvo) {
      return;
    }
    this.trocarAlvo.set(null);
    this.salvarCalendario(alvo.numPlbnf);
  }

  private salvarCalendario(numPlbnf: number): void {
    const codCltrprf = this.escolhaPorPlano()[numPlbnf] ?? null;
    this.salvandoCalendario.set(numPlbnf);

    this.deParaService
      .salvarCalendario(this.idCampanha(), numPlbnf, codCltrprf)
      .pipe(finalize(() => this.salvandoCalendario.set(null)))
      .subscribe({
        next: (plano) => {
          this.toastService.show('Calendário salvo com sucesso.', 'success');
          this.planosCalendarioSignal.update((planos) =>
            planos.map((p) => (p.numPlbnf === numPlbnf ? { ...p, codCltrprf: plano.codCltrprf } : p)),
          );
          this.escolhaPorPlanoSignal.update((atual) => ({ ...atual, [numPlbnf]: plano.codCltrprf }));
        },
        error: (error: unknown) => this.mostrarErro(error, 'Não foi possível salvar o calendário do plano.'),
      });
  }

  onFechar(): void {
    this.close.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.trocarAlvo()) {
      this.onCancelarTroca();
      return;
    }
    if (this.excluirAlvo()) {
      this.onCancelarExclusao();
      return;
    }
    if (this.formAberto()) {
      this.onCancelarForm();
      return;
    }
    this.onFechar();
  }

  onAbrirNovo(): void {
    this.modoEdicao.set(false);
    this.formAberto.set(true);
    this.perfilSelecionado.set(null);
    this.planoSelecionado.set(null);
    this.codigoSelecionado.set(null);
    this.catalogCodigosSignal.set([]);
  }

  onEditar(config: DeParaIntegracao): void {
    this.modoEdicao.set(true);
    this.formAberto.set(true);
    this.perfilSelecionado.set({ idPerfilInvest: config.idPerfilInvest, nome: config.nomePerfil });
    this.planoSelecionado.set({ numPlbnf: config.numPlbnf, nome: config.nomePlano });
    this.codigoSelecionado.set(null);
    this.carregarCodigosDoPlano(config.numPlbnf);
  }

  onCancelarForm(): void {
    this.formAberto.set(false);
  }

  onPerfilSelectChange(event: Event): void {
    const id = Number((event.target as HTMLSelectElement).value);
    this.perfilSelecionado.set(this.perfisDisponiveis().find((p) => p.idPerfilInvest === id) ?? null);
    this.planoSelecionado.set(null);
    this.codigoSelecionado.set(null);
    this.catalogCodigosSignal.set([]);
  }

  onPlanoSelectChange(event: Event): void {
    const numPlbnf = Number((event.target as HTMLSelectElement).value);
    const plano = this.planosDisponiveis().find((p) => p.numPlbnf === numPlbnf) ?? null;
    this.planoSelecionado.set(plano);
    this.codigoSelecionado.set(null);
    this.catalogCodigosSignal.set([]);
    if (plano) {
      this.carregarCodigosDoPlano(plano.numPlbnf);
    }
  }

  onCodigoSelectChange(event: Event): void {
    const codPrfinv = Number((event.target as HTMLSelectElement).value);
    this.codigoSelecionado.set(this.codigosDisponiveis().find((c) => c.codPrfinv === codPrfinv) ?? null);
  }

  onSalvar(): void {
    const perfil = this.perfilSelecionado();
    const plano = this.planoSelecionado();
    const codigo = this.codigoSelecionado();
    if (!perfil || !plano || !codigo || this.salvando()) {
      return;
    }

    this.salvando.set(true);

    const request$ = this.modoEdicao()
      ? this.deParaService.atualizar(this.idCampanha(), perfil.idPerfilInvest, plano.numPlbnf, codigo.codPrfinv)
      : this.deParaService.criar(this.idCampanha(), perfil.idPerfilInvest, plano.numPlbnf, codigo.codPrfinv);

    request$.pipe(finalize(() => this.salvando.set(false))).subscribe({
      next: () => {
        this.toastService.show('Configuração de integração salva com sucesso.', 'success');
        this.formAberto.set(false);
        this.recarregarConfigs();
      },
      error: (error: unknown) => this.mostrarErro(error, 'Não foi possível salvar a configuração de integração.'),
    });
  }

  onExcluirClick(config: DeParaIntegracao): void {
    this.excluirAlvo.set(config);
  }

  onCancelarExclusao(): void {
    this.excluirAlvo.set(null);
  }

  onConfirmarExclusao(): void {
    const alvo = this.excluirAlvo();
    if (!alvo) {
      return;
    }
    this.excluirAlvo.set(null);

    this.deParaService.excluir(this.idCampanha(), alvo.idPerfilInvest, alvo.numPlbnf).subscribe({
      next: () => {
        this.toastService.show('Configuração de integração removida com sucesso.', 'success');
        this.recarregarConfigs();
      },
      error: (error: unknown) => this.mostrarErro(error, 'Não foi possível remover a configuração de integração.'),
    });
  }

  private carregarTudo(): void {
    this.carregando.set(true);

    forkJoin({
      configs: this.deParaService.listar(this.idCampanha()),
      pares: this.deParaService.listarParesPerfilPlano(this.idCampanha()),
      calendarios: this.deParaService.listarCalendarios(this.idCampanha()),
    })
      .pipe(finalize(() => this.carregando.set(false)))
      .subscribe({
        next: ({ configs, pares, calendarios }) => {
          this.configsSignal.set(configs);
          this.catalogParesSignal.set(pares);
          this.aplicarCalendarios(calendarios);
        },
        error: (error: unknown) =>
          this.mostrarErro(error, 'Não foi possível carregar a configuração de integração da campanha.'),
      });
  }

  /**
   * A aba Calendário se alimenta do De-Para: os planos vêm das linhas configuradas e os
   * calendários ofertados são filtrados por esses planos. Criar ou excluir uma linha muda as
   * duas abas, então as duas recarregam juntas — senão o select de calendário fica vazio até
   * reabrir a modal.
   */
  private recarregarConfigs(): void {
    forkJoin({
      configs: this.deParaService.listar(this.idCampanha()),
      calendarios: this.deParaService.listarCalendarios(this.idCampanha()),
    }).subscribe({
      next: ({ configs, calendarios }) => {
        this.configsSignal.set(configs);
        this.aplicarCalendarios(calendarios);
      },
      error: (error: unknown) => this.mostrarErro(error, 'Não foi possível atualizar a lista de configurações.'),
    });
  }

  private aplicarCalendarios(calendarios: CalendariosCampanha): void {
    this.planosCalendarioSignal.set(calendarios.planos);
    this.calendariosSignal.set(calendarios.calendarios);
    this.escolhaPorPlanoSignal.set(
      Object.fromEntries(calendarios.planos.map((p) => [p.numPlbnf, p.codCltrprf])),
    );
  }

  private carregarCodigosDoPlano(numPlbnf: number): void {
    this.carregandoCodigos.set(true);

    this.deParaService
      .listarCodigosAmadeus(numPlbnf)
      .pipe(finalize(() => this.carregandoCodigos.set(false)))
      .subscribe({
        next: (codigos) => this.catalogCodigosSignal.set(codigos),
        error: (error: unknown) => this.mostrarErro(error, 'Não foi possível carregar os códigos Amadeus do plano.'),
      });
  }

  private mostrarErro(error: unknown, fallback: string): void {
    const message = error instanceof ApiHttpError ? error.message : fallback;
    this.toastService.show(message, 'error');
  }
}
